import { cn } from '@/lib/cn';
import { Check, ChevronDown, ChevronUp, XIcon } from 'lucide-react';
import Select, {
  components,
  type GroupBase,
  type OptionProps,
  type Props,
} from 'react-select';

interface DropdownSelectProps {
  preffix?: React.ReactNode;
  isError?: boolean;
  /**
   * Draws a checkbox on every option, so a multi-select says it takes more
   * than one before the operator has picked anything. Opt-in rather than
   * automatic on `isMulti`: the box is the right affordance for a list you
   * tick through, and the wrong one for a field where the menu is a picker and
   * the chosen values live entirely in the control.
   *
   * The box is drawn, not built from the `Checkbox` primitive: a real
   * `role="checkbox"` inside a `role="option"` is a nested control screen
   * readers have to reconcile, and the option row already reports its own
   * selected state. Pair it with `hideSelectedOptions={false}`, or a ticked
   * row vanishes the moment it is ticked.
   */
  withCheckboxes?: boolean;
}

function DropdownSelect<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  preffix,
  isError = false,
  withCheckboxes = false,
  ...props
}: Props<Option, IsMulti, Group> & DropdownSelectProps) {
  const CheckboxOption = ({
    children,
    ...optionProps
  }: OptionProps<Option, IsMulti, Group>) => (
    <components.Option {...optionProps}>
      <span className="flex items-center gap-2">
        <span
          aria-hidden
          className={cn(
            'border-border-color flex size-4 shrink-0 items-center justify-center rounded border shadow-xs',
            optionProps.isSelected &&
              'bg-primary-70 border-primary-70 text-white'
          )}
        >
          {optionProps.isSelected && (
            <Check className="size-3" strokeWidth={3} />
          )}
        </span>
        {children}
      </span>
    </components.Option>
  );

  return (
    <Select
      unstyled
      {...props}
      styles={{
        control: (base, state) => ({
          ...base,
          cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        }),
        input: (base, state) => ({
          ...base,
          cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        }),
        ...props.styles,
      }}
      components={{
        DropdownIndicator: props => {
          return (
            <components.DropdownIndicator {...props}>
              {props.selectProps.menuIsOpen ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
            </components.DropdownIndicator>
          );
        },
        ClearIndicator: props => {
          return (
            <components.ClearIndicator {...props}>
              <XIcon size={14} />
            </components.ClearIndicator>
          );
        },
        Option: withCheckboxes ? CheckboxOption : components.Option,
        Control: ({ children, ...props }) => {
          return (
            <components.Control {...props} className="gap-2">
              {preffix}
              {children}
            </components.Control>
          );
        },
        ...props.components,
      }}
      classNames={{
        clearIndicator: () => cn('text-text-primary'),
        container: () => cn('w-full'),
        control: controlProps =>
          cn(
            'border-border-color rounded-md border bg-neutral-100 px-3 py-2 text-sm shadow-none',
            controlProps.isFocused && 'border-neutral-50',
            controlProps.isDisabled && 'bg-slate-50',
            isError && 'border-red-500 focus:border-red-600',
            props.classNames?.control?.(controlProps)
          ),
        dropdownIndicator: () => cn('text-text-primary'),
        // group: () => cn(),
        // groupHeading: props => cn(),
        // indicatorsContainer: props => cn(),
        // indicatorSeparator: props => cn(),
        input: () => cn('text-text-primary'),
        // loadingIndicator: props => cn(),
        // loadingMessage: props => cn(),
        menu: () =>
          cn(
            'border-border-default my-2 rounded-md border bg-white px-1 py-1 text-sm'
          ),
        // menuList: props => cn(),
        // menuPortal: props => cn(),
        multiValue: () =>
          cn('bg-neutral-90 px-1 py-0.5 my-0.5 text-xs rounded mr-1.5'),
        // multiValueLabel: props => cn(''),
        // multiValueRemove: props => cn(),
        noOptionsMessage: () => cn('text-sm p-1'),
        option: ({ isFocused, isSelected, isDisabled }) =>
          cn(
            'text-text-tertiary rounded-md px-2 py-1.5',
            isFocused && 'bg-neutral-80',
            // isSelected && 'bg-neutral-10',
            !isDisabled && isSelected && 'text-text-primary'
          ),
        placeholder: ({ isDisabled }) =>
          cn('text-text-placeholder', isDisabled && 'text-text-disabled'),
        // singleValue: () => cn(),
        valueContainer: () => cn('text-text-primary'),
      }}
    />
  );
}

export { DropdownSelect };
